import { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { Box, useTheme } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { htmlToQuillDelta } from './quillHtmlToDelta.ts';
import { convertQuillHtmlToInlineAndClassHtml } from './convertQuillHtmlToInlineAndClassHtml.js';

export const fontSizes = ['6px', '8px', '10px', '12px', '15px', '18px', '24px'];
export const fontFamilies = ['montserrat'];
export const DEFAULT_FONT_SIZE = '12px';

// FONT: use class-based attribution
const Font = Quill.import('attributors/class/font');
Font.whitelist = fontFamilies;
Quill.register(Font, true);

// SIZE: use class-based attribution (not style!)
const Size = Quill.import('attributors/class/size');
Size.whitelist = fontSizes;
Quill.register(Size, true);

const Editor = forwardRef(
  (
    {
      readOnly,
      defaultValue,
      onTextChange,
      onSelectionChange,
      onHtmlChange,
      hasError,
      afterComponent,
      defaultColor,
      defaultFontSize,
      bgcolor,
      lang,
    },
    ref,
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

    const containerRef = useRef(null);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const onHtmlChangeRef = useRef(onHtmlChange);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
      onHtmlChangeRef.current = onHtmlChange;
    });

    useEffect(() => {
      ref.current?.enable(!readOnly);
    }, [ref, readOnly]);

    useEffect(() => {
      const container = containerRef.current;
      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div'),
      );
      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules: {
          toolbar: {
            container: '#custom-toolbar', // ID of your MUI toolbar container
          },
        },
      });

      ref.current = quill;

      // Enable browser-native spellcheck and set language
      // Use DOM properties (not just attributes) for reliable browser support
      quill.root.spellcheck = true;
      quill.root.setAttribute('lang', lang || 'de');
      // iOS Safari: enable autocorrect and autocapitalize
      quill.root.setAttribute('autocorrect', 'on');
      quill.root.setAttribute('autocapitalize', 'sentences');

      const effectiveDefaultSize = defaultFontSize || DEFAULT_FONT_SIZE;

      if (defaultValue) {
        quill.setContents(htmlToQuillDelta(defaultValue));
      }

      // Set default font size as real Quill format so new typing matches the dropdown
      quill.setSelection(0, 0, 'silent');
      quill.format('size', effectiveDefaultSize);

      // iOS Composition Guard: prevent duplicate text from iOS autocomplete/dictation
      let isComposing = false;
      let pendingHtmlUpdate = false;

      quill.root.addEventListener('compositionstart', () => {
        isComposing = true;
      });

      quill.root.addEventListener('compositionend', () => {
        isComposing = false;
        // Fire deferred HTML change after composition completes
        if (pendingHtmlUpdate) {
          pendingHtmlUpdate = false;
          const quillHtml = quill.root.innerHTML;
          const plainText = quill.getText().trim();
          const inlineHtml = convertQuillHtmlToInlineAndClassHtml(
            quillHtml,
            effectiveDefaultSize,
          );
          onHtmlChangeRef.current?.(inlineHtml, plainText);
        }
      });

      let lastActiveSize = effectiveDefaultSize;

      quill.on(Quill.events.TEXT_CHANGE, (delta, oldDelta, source) => {
        onTextChangeRef.current?.(delta, oldDelta, source);

        // Track last active size (for paste normalization) without forcing it
        const currentFormat = quill.getFormat();
        if (currentFormat.size) {
          lastActiveSize = currentFormat.size;
        }

        // Apply size to user-typed inserts that came in without a size attribute.
        // This catches the case where the caret sits at a span boundary and Quill
        // emits a bare text op — without this, those runs export as bare text nodes
        // adjacent to the existing sized span.
        if (source === 'user' && lastActiveSize) {
          let index = 0;
          delta.ops.forEach((op) => {
            if (op.retain != null) {
              index += op.retain;
              return;
            }
            if (op.delete != null) {
              return;
            }
            if (op.insert == null) return;
            if (typeof op.insert !== 'string') {
              index += 1;
              return;
            }
            const text = op.insert;
            const hasSize = op.attributes && op.attributes.size;
            if (!hasSize && text.length > 0 && !text.includes('\n')) {
              quill.formatText(
                index,
                text.length,
                'size',
                lastActiveSize,
                'silent'
              );
            }
            index += text.length;
          });
        }

        // Defer HTML processing during composition (iOS autocomplete/dictation)
        if (isComposing) {
          pendingHtmlUpdate = true;
          return;
        }

        const quillHtml = quill.root.innerHTML;
        const plainText = quill.getText().trim();
        const inlineHtml = convertQuillHtmlToInlineAndClassHtml(
            quillHtml,
            effectiveDefaultSize,
          );
        onHtmlChangeRef.current?.(inlineHtml, plainText);
      });

      quill.on(Quill.events.SELECTION_CHANGE, (range, oldRange, source) => {
        // Only track last active size; do not force it onto empty lines
        // or onto mixed-size selections (which report size === undefined).
        if (range) {
          const currentFormat = quill.getFormat(range);
          if (currentFormat.size) {
            lastActiveSize = currentFormat.size;
          }
        }
        onSelectionChangeRef.current?.(range, oldRange, source);
      });

      // PASTE HANDLING: Strip font and color; force a size so no bare text nodes remain
      const normalizePastedOps = (delta) => {
        const forcedSize = lastActiveSize || effectiveDefaultSize;

        delta.ops.forEach((op) => {
          // Skip embeds (images, etc.) and pure newline ops
          if (typeof op.insert !== 'string') return;
          if (op.insert === '\n') return;

          const attrs = op.attributes;
          if (attrs) {
            delete attrs.font;
            delete attrs.color;
            delete attrs.code;
            delete attrs.background;
            delete attrs['code-block'];
          }

          if (forcedSize) {
            if (!op.attributes) op.attributes = {};
            op.attributes.size = forcedSize;
          } else if (attrs) {
            delete attrs.size;
          }
        });
        return delta;
      };

      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) =>
        normalizePastedOps(delta),
      );
      quill.clipboard.addMatcher(Node.TEXT_NODE, (_node, delta) =>
        normalizePastedOps(delta),
      );

      return () => {
        ref.current = null;
        container.innerHTML = '';
      };
    }, [ref, defaultValue]);

    return (
      <Box
        sx={{
          borderLeft: `4px solid ${hasError ? colors.warning : colors.success}`,
          borderRadius: '12px',
          backgroundColor: bgcolor || colors.bgInput,
          padding: 2,
          '.ql-container': {
            border: '0px solid transparent',
          },
          '.ql-editor': {
            fontSize: defaultFontSize || DEFAULT_FONT_SIZE,
            lineHeight: '1.5',
            padding: 0,
            minHeight: '200px',
            fontFamily: 'montserrat',
            color: 'inherit', // Text color inherits from theme/parent
          },
          '.ql-editor ol, .ql-editor ol li': {
            fontFamily: 'inherit',
            fontSize: 'inherit',
          },
          ol: {
            padding: '0px',
            margin: '0px',
          },
          'li:has(.ql-size-6px)': {
            fontSize: '6px!important',
          },
          'li:has(.ql-size-8px)': {
            fontSize: '8px!important',
          },
          'li:has(.ql-size-10px)': {
            fontSize: '10px!important',
          },
          'li:has(.ql-size-12px)': {
            fontSize: '12px!important',
          },
          'li:has(.ql-size-15px)': {
            fontSize: '15px!important',
          },
          'li:has(.ql-size-18px)': {
            fontSize: '18px!important',
          },
          'li:has(.ql-size-24px)': {
            fontSize: '24px!important',
          },
          '.ql-font-montserrat': {
            fontFamily: 'montserrat',
          },
        }}
        className=" overflow-y-auto"
      >
        <div ref={containerRef}></div> <Box className="px-4" />
        {afterComponent}
      </Box>
    );
  },
);

Editor.displayName = 'Editor';

export default Editor;
